import checkCircleIcon from "@assets/images/check-circle.png";
import infoIcon from "@assets/images/info.png";
import iconRefresh from "@assets/images/refresh.svg";
import iconSearch from "@assets/images/Search.png";
import closeIcon from "@assets/images/x-circle.png";
import { Box, Button, Typography } from "@material-ui/core";
import { useUser } from "@saleor/auth";
import Autocomplete from "@saleor/components/Autocomplete";
import MyTypography from "@saleor/components/MyTypography";
import { NumberInput } from "@saleor/components/NumberInput";
import SingleTextField from "@saleor/components/SingleTextField";
import {
  useExportSoldTosLazyQuery,
  useGetCustomerBlockLazyQuery,
  useSearchSuggestionDomesticSoldTosLazyQuery
} from "@saleor/graphql";
import { makeStyles } from "@saleor/macaw-ui";
import { mapEdgesToItems } from "@saleor/utils/maps";
import classNames from "classnames";
import uniqBy from "lodash/uniqBy";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

interface SoldToSearchBarProps {
  value?: string;
  onchange: (value: string) => void;
  onReset: () => void;
  searchId: string;
  loading?: boolean;
  dataFound?: boolean;
  soldToExists?: boolean;
  isDomestic?: boolean;
}

const useStyles = makeStyles(
  theme => ({
    root: {
      borderRadius: 6,
      background: "white",
      boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
      overflow: "hidden"
    },
    header: {
      padding: "1rem 1.6rem",
      background: theme.palette.gardient.medium,
      colors: "white",
      display: "flex",
      alignItems: "center"
    },
    title: {
      color: "white",
      marginRight: "4px"
    },
    searchBox: {
      padding: "1.6rem"
    },
    searchInput: {
      display: "flex"
    },
    info: {
      marginTop: "2rem",
      display: "flex",
      alignItems: "center",
      "& img": {
        marginRight: "0.7rem"
      }
    },
    button: {
      height: "4rem",
      minWidth: "auto",
      backgroundColor: theme.palette.grey[900],
      "&:active": {
        background: theme.palette.grey[700],
        outline: "none",
        border: `${theme.palette.grey[900]} solid 3px`,
        borderRadius: "4px",
        transition: "0s"
      },
      "&:hover": {
        backgroundColor: theme.palette.grey[700]
      }
    },
    formControl: {
      display: "grid",
      gridTemplateColumns: "auto 48px",
      gridGap: "1rem",
      // alignItems: "end",
      "& .MuiInputLabel-outlined": {
        transform: "translate(14px, 11px) scale(1)",
        "&.MuiInputLabel-shrink": {
          transform: "translate(12px, 7px) scale(0.75)"
        }
      }
    },
    errorMessage: {
      color: theme.palette.error.light,
      lineHeight: "18px",
      marginTop: 4
    },
    successMessage: {
      color: theme.palette.success.main
    },
    cannotClick: {
      border: `1px solid ${theme.palette.background.default}`,
      background: theme.palette.background.default,
      pointerEvents: "none",
      "& fieldset": {
        borderColor: theme.palette.background.default
      },
      "& input": {
        color: theme.palette.grey[900]
      }
    },
    inputRoot: {
      height: "40px"
    }
  }),
  { name: "SoldToSearchBar" }
);

enum MessageMode {
  WAITING = "WAITING",
  SOLD_TO_NOT_FOUND = "SOLD_TO_NOT_FOUND",
  SOLD_TO_EXISTS = "SOLD_TO_EXISTS",
  CUSTOMER_BLOCK = "CUSTOMER_BLOCK"
}

const SoldToSearchBar: React.FC<SoldToSearchBarProps> = ({
  searchId,
  onchange,
  onReset,
  loading,
  dataFound = false,
  soldToExists = false,
  isDomestic
}) => {
  const [error, setError] = useState<boolean>(false);
  const [value, setValue] = useState<string>(searchId);
  const [valueSoldToSelected, setValueSoldToSelected] = useState<string>("");
  const [mode, setMode] = useState<MessageMode>(MessageMode.WAITING);
  const classes = useStyles();
  const { user } = useUser();
  const intl = useIntl();

  const [getCustomerBlock] = useGetCustomerBlockLazyQuery({
    fetchPolicy: "no-cache",
    onCompleted: data => {
      if (!data?.getCustomerBlock?.customerBlock) {
        if (valueSoldToSelected) {
          onchange(valueSoldToSelected.substr(0, 10));
        }
        if (value) {
          onchange(value);
        }
        return;
      }
      setMode(MessageMode.CUSTOMER_BLOCK);
    }
  });

  const handleSearch = () => {
    if (!value && !valueSoldToSelected) {
      setError(true);
      return;
    }
    if (user?.isStaff) {
      if (valueSoldToSelected) {
        const soldToCode = valueSoldToSelected.substr(0, 10);
        getCustomerBlock({ variables: { input: { soldToCode } } });
      }
      if (value) {
        getCustomerBlock({ variables: { input: { soldToCode: value } } });
      }
    } else {
      if (value) {
        onchange(value);
      } else {
        setError(true);
      }
    }
  };

  const [
    getDataSuggestions,
    { data: fetchDataSuggestions, loading: loadingDataSuggestion }
  ] = useExportSoldTosLazyQuery();

  const [
    getDataSuggestionsDemostic,
    {
      data: fetchDataSuggestionsDemostic,
      loading: loadingDataSuggestionDemostic
    }
  ] = useSearchSuggestionDomesticSoldTosLazyQuery();

  const handleReset = () => {
    setError(false);
    setValue("");
    onReset();
  };

  useEffect(() => {
    if (isDomestic) {
      getDataSuggestionsDemostic({
        variables: {
          first: 100,
          filter: {
            search: value || searchId
          }
        }
      });
      return;
    }
    getDataSuggestions({
      variables: {
        first: 100,
        filter: {
          search: value || searchId
        }
      }
    });
  }, [value]);

  const dataOptions = useMemo(() => {
    let data = [];
    if (fetchDataSuggestions?.exportSoldTos) {
      data = mapEdgesToItems(fetchDataSuggestions?.exportSoldTos);
    }
    if (fetchDataSuggestionsDemostic?.searchSuggestionDomesticSoldTos) {
      data = mapEdgesToItems(
        fetchDataSuggestionsDemostic?.searchSuggestionDomesticSoldTos
      );
    }
    const res = data.map(data => ({
      title: data.displayText,
      value: data.code
    }));

    return uniqBy(res, "value");
  }, [
    fetchDataSuggestions?.exportSoldTos,
    fetchDataSuggestionsDemostic?.searchSuggestionDomesticSoldTos
  ]);

  const handleSelectedSearch = value => {
    setValueSoldToSelected(value);
    if (value) {
      setError(false);
    }
  };

  useEffect(() => {
    if (loading) {
      setMode(MessageMode.WAITING);
      return;
    }
    if (searchId && !dataFound && !loading) {
      setMode(MessageMode.SOLD_TO_NOT_FOUND);
      return;
    }
    if (soldToExists) {
      setMode(MessageMode.SOLD_TO_EXISTS);
      return;
    }
  }, [dataFound, searchId, soldToExists, loading]);

  const handleChangeSearchCustomer = event => {
    setValue(event.target.value);
    if (event.target.value) {
      setError(false);
    }
  };

  const renderSearchBoxDomestic = (
    <>
      {searchId ? (
        <SingleTextField
          value={
            dataOptions.filter(data => data.value === searchId)[0]?.title ||
            searchId
          }
          onChange={() => null}
          readOnly
          id="soldToSearch"
          name="soldToSearch"
        />
      ) : (
        <Autocomplete
          optionsProp={dataOptions}
          placeholderProps={
            user?.isStaff ? "กรอกรหัสลูกค้า" : "กรอกเลขที่ Contract"
          }
          onChangeProps={setValue}
          loading={loadingDataSuggestion || loadingDataSuggestionDemostic}
          onSearch={handleSelectedSearch}
          handleSearch={handleSearch}
          maxLengthProps={10}
          numberOnly
          id="soldToSearch"
          name="soldToSearch"
        />
      )}
    </>
  );

  const renderSearchBoxCustomer = (
    <>
      <NumberInput
        onChange={handleChangeSearchCustomer}
        placeholder={"กรอกเลขที่ Contract"}
        error={error}
        value={value}
        className={classNames(classes.inputRoot, {
          [classes.cannotClick]: searchId
        })}
        maxLength={10}
        id="soldToSearch"
      />
    </>
  );

  return (
    <>
      {!user?.isStaff && (
        <MyTypography variant="h7" style={{ marginBottom: "1.6rem" }}>
          {intl.formatMessage({
            defaultMessage: "ค้นหา"
          })}
        </MyTypography>
      )}
      <Box className={classes.root}>
        <div className={classes.header}>
          <Typography variant="h6" className={classes.title}>
            {user?.isStaff
              ? intl.formatMessage({
                  defaultMessage: "SOLD TO"
                })
              : intl.formatMessage({
                  defaultMessage: "เลขที่ Contract"
                })}
          </Typography>
          {user?.isStaff && <span className={classes.title}>(Required)</span>}
        </div>
        <div className={classes.searchBox}>
          <div className={classes.formControl}>
            {user?.isStaff ? renderSearchBoxDomestic : renderSearchBoxCustomer}
            {!searchId ? (
              <Button
                variant="contained"
                color="secondary"
                className={classes.button}
                onClick={handleSearch}
                id="soldToSearchButton"
                name="soldToSearchButton"
              >
                <img src={iconSearch} alt="search" />
              </Button>
            ) : (
              <Button
                variant="contained"
                color="secondary"
                className={classes.button}
                onClick={handleReset}
                id="soldToResetButton"
                name="soldToResetButton"
              >
                <img src={iconRefresh} alt="search" />
              </Button>
            )}
          </div>
          {error ? (
            user?.isStaff ? (
              <MyTypography className={classes.errorMessage} variant="body3">
                {intl.formatMessage({ defaultMessage: "กรุณากรอกรหัสลูกค้า" })}
              </MyTypography>
            ) : (
              <MyTypography className={classes.errorMessage} variant="caption">
                {intl.formatMessage({
                  defaultMessage: "กรุณากรอกข้อมูลที่ต้องการค้นหา"
                })}
              </MyTypography>
            )
          ) : null}
          {user?.isStaff ? (
            <div className={classes.info}>
              {mode === MessageMode.WAITING && (
                <>
                  <img src={infoIcon} alt="info" />
                  <MyTypography variant="body3">
                    {intl.formatMessage({
                      defaultMessage: "กรุณากรอกรหัสลูกค้าและกดค้นหา"
                    })}
                  </MyTypography>
                </>
              )}
              {mode === MessageMode.SOLD_TO_EXISTS && (
                <>
                  <img src={checkCircleIcon} alt="success" />
                  <MyTypography
                    variant="body3"
                    className={classes.successMessage}
                  >
                    {intl.formatMessage({
                      defaultMessage: "พบรายการที่เกี่ยวข้อง"
                    })}
                  </MyTypography>
                </>
              )}
              {mode === MessageMode.SOLD_TO_NOT_FOUND && (
                <>
                  <img src={closeIcon} alt="close" />
                  <MyTypography
                    className={classes.errorMessage}
                    variant="body3"
                  >
                    {intl.formatMessage({
                      defaultMessage: "ไม่พบรายการที่เกี่ยวข้อง"
                    })}
                  </MyTypography>
                </>
              )}
              {mode === MessageMode.CUSTOMER_BLOCK && (
                <>
                  <img src={closeIcon} alt="close" />
                  <MyTypography
                    className={classes.errorMessage}
                    variant="body3"
                  >
                    {intl.formatMessage({
                      defaultMessage: "Customer Block"
                    })}
                  </MyTypography>
                </>
              )}
            </div>
          ) : null}
        </div>
      </Box>
    </>
  );
};

export default SoldToSearchBar;
